// frontend/src/utils/monthlyReportFormat.js
// Etiquetas (castellano) y unidades del bloque de cifras del cierre mensual
// (Informes → «Cierre mensual»). Única fuente para Reports.jsx: el dataset
// del backend (utils/monthly_report.py) viaja con sus claves internas y aquí
// se traducen y se formatean con la unidad correcta.
//
// Smoke 30-08-2026 (B4): antes las claves se pintaban en crudo («Margin pct»,
// «Metric value», «Threshold») y todo número era euros — incluidos los meses
// de runway, los puntos de caída del margen y los porcentajes de las
// tensiones.
import { formatCurrency } from "@/utils/format";

// Claves internas que jamás se muestran (ids).
export const HIDDEN_METRIC_KEYS = new Set([
  "service_id",
  "tension_id",
  "budget_id",
  "client_id",
]);

export const METRIC_LABELS = {
  // resultado_mes
  month: "Mes",
  ingresos: "Ingresos",
  gastos: "Gastos",
  margen: "Margen",
  margen_pct: "Margen %",
  mes_anterior: "Mes anterior",
  delta: "Variación vs mes anterior",
  eur: "€",
  pct: "%",
  // presupuesto_vs_real
  budget_name: "Presupuesto",
  categorias: "Partidas",
  categoria: "Partida",
  presupuestado: "Presupuestado",
  real: "Real",
  desviacion_eur: "Desviación (€)",
  desviacion_pct: "Desviación (%)",
  top_desviaciones: "Mayores desviaciones",
  // tesoreria
  saldo_fin_mes: "Saldo a fin de mes",
  saldo_fecha: "Fecha del saldo",
  prevision_fin_mes_siguiente: "Previsión a fin del mes siguiente",
  saldo_previsto: "Saldo previsto",
  ingresos_previstos: "Ingresos previstos",
  gastos_previstos: "Gastos previstos",
  media_ingresos_6m: "Media de ingresos (6 meses hasta el mes)",
  media_gastos_6m: "Media de gastos (6 meses hasta el mes)",
  suscripciones_mensuales: "Suscripciones mensuales (activas)",
  // cobros_pendientes
  total_pendiente: "Total pendiente",
  total_vencido: "Total vencido",
  top_pendientes: "Principales cobros pendientes",
  moneda_extranjera_excluidas: "Facturas en divisa excluidas",
  cliente: "Cliente",
  invoice_number: "Nº factura",
  pendiente: "Pendiente",
  due_date: "Vencimiento",
  dias_vencido: "Días vencido",
  // margenes (informe: servicios vigentes en el mes; chat: top/bottom)
  servicios: "Servicios vigentes en el mes",
  servicios_sin_costes_imputados: "Servicios sin costes imputados",
  sin_costes_imputados: "Sin costes imputados",
  sin_servicio: "Sin servicio",
  clientes_sin_servicio: "Clientes facturados sin servicio asignado",
  estado: "Estado",
  inicio: "Inicio",
  fin: "Fin",
  top: "Mejor margen",
  bottom: "Peor margen",
  service_name: "Servicio",
  client_name: "Cliente",
  revenue: "Ingresos",
  total_costs: "Costes",
  margin: "Margen",
  margin_pct: "Margen %",
  // vigilar
  tensiones: "Tensiones",
  risk_score: "Puntuación de riesgo",
  global_score: "Riesgo global",
  label: "Situación",
  severity: "Gravedad",
  title: "Alerta",
  detail: "Detalle",
  metric_value: "Valor actual",
  threshold: "Umbral de alerta",
};

const MONTHS_ES = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];

function humanize(key) {
  const s = String(key).replace(/_/g, " ").trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function labelForMetricKey(key) {
  return METRIC_LABELS[key] ?? humanize(key);
}

// "2026-08" → "agosto 2026"
export function formatMonth(ym) {
  const m = /^(\d{4})-(\d{2})$/.exec(String(ym ?? ""));
  if (!m) return String(ym ?? "");
  const idx = Number(m[2]) - 1;
  return MONTHS_ES[idx] ? `${MONTHS_ES[idx]} ${m[1]}` : String(ym);
}

// "2026-09-15" → "15/09/2026"
function formatIsoDate(s) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s ?? ""));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : String(s ?? "");
}

function formatNumber(n, decimals = 1) {
  return Number(n).toLocaleString("es-ES", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

// Enteros sin decimales ("3"), decimales con una cifra ("1,2").
function formatPlain(n) {
  return Number.isInteger(Number(n)) ? formatNumber(n, 0) : formatNumber(n, 1);
}

// "30 %", "44,7 %", "-501,9 %"
function formatPercent(n) {
  return `${formatPlain(n)} %`;
}

const COUNT_KEYS = new Set(["moneda_extranjera_excluidas", "count"]);

export function formatMetricValue(key, value) {
  if (value == null) return "-";
  if (typeof value === "boolean") return value ? "Sí" : "No";
  const k = String(key);
  if (typeof value === "string") {
    if (k === "month" || /^\d{4}-\d{2}$/.test(value)) return formatMonth(value);
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) return formatIsoDate(value);
    return value;
  }
  if (typeof value === "number") {
    if (/(^|_)pct$/.test(k) || /(percent|porcentaje)/.test(k)) return formatPercent(value);
    if (/^dias(_|$)|_dias$/.test(k)) return `${formatPlain(value)} días`;
    if (k === "global_score") return `${formatPlain(value)}/100`;
    if (COUNT_KEYS.has(k) || /(^|_)(count|num|numero|score)(_|$)/.test(k)) return formatPlain(value);
    return formatCurrency(value);
  }
  return String(value);
}

// Unidad de metric_value / threshold por detector (utils/cfo/tensions.py).
// margin_decline: el valor es el margen ACTUAL (%) y el umbral la caída
// mínima en puntos que dispara la alerta — unidades distintas a propósito.
const TENSION_UNITS = {
  cash_runway_short: { value: "meses", threshold: "meses" },
  margin_decline: { value: "%", threshold: "puntos" },
  client_concentration: { value: "%", threshold: "%" },
  costs_outpacing_revenue: { value: "meses", threshold: "meses" },
  delinquency_increasing: { value: "días", threshold: "días" },
  subscriptions_disproportionate: { value: "%", threshold: "%" },
  revenue_stagnation: { value: "%", threshold: "%" },
  anomalous_expense: { value: "€", threshold: "€" },
};

export function formatTensionMetric(tensionId, kind, value) {
  if (value == null) return "-";
  const unit = TENSION_UNITS[tensionId]?.[kind];
  if (unit === "€") return formatCurrency(value);
  if (unit === "%") return formatPercent(value);
  if (unit) return `${formatPlain(value)} ${unit}`;
  return formatPlain(value);
}

const SEVERITY_LABELS = {
  critical: "Crítica",
  important: "Importante",
  informative: "Informativa",
};

export function severityLabel(severity) {
  return SEVERITY_LABELS[severity] ?? String(severity ?? "");
}
